import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { useToast } from '_/hooks/use-toast'

import { Button, FormControlLabel, Grid, InputLabel, Typography } from '@mui/material'
import { Loader } from '@refera/ui-web'

import HeaderTitle from '_/components/header-title'

import useStyles from './styles'

import { navigate, useParams } from '@reach/router'

import { getProfileById } from '_/modules/profile/actions'
import { getProfileSelector } from '_/modules/profile/selectors'
import useRolePermission from '_/hooks/use-role-permission'
import { PERMISSIONS, PERMISSIONS_ACTIONS } from '_/utils/constants/permissions'
import { BasicInput } from '_/components/inputs'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import HelpInfoButton from '_/components/button/help-info-button'
import { editProfile } from '_/modules/authentication/actions'
import SwitchComponent from '_/components/switch'

export function EditProfiles() {
  const profile = useSelector(getProfileSelector)

  const { perfilId } = useParams()

  const { checkIfUserDoesNotPermission } = useRolePermission()

  const dispatch = useDispatch()
  const styles = useStyles()
  const [isLoading, setIsLoading] = useState(false)

  const perfilName = useMemo(() => {
    const name = profile?.desc

    return name ?? ''
  }, [profile, perfilId])

  const methods = useForm({
    mode: 'all',
    defaultValues: {
      perfilName: perfilName ?? '',
      specialStepFilter: profile?.specialStepFilter ?? false,
      intermediaryPerfil: profile?.intermediaryPerfil ?? false,
      areaOfActivityDefined: profile?.areaOfActivityDefined ?? false,
      urlPainel: profile?.urlPainel ?? '',
      displayAgencies: profile?.displayAgencies ?? false,
      resourceAntecipation: profile?.resourceAntecipation ?? false,
      bankingIntegrations: profile?.bankingIntegrations ?? false,
    },
  })

  const { handleSubmit, setValue, control, watch } = methods

  const areaOfActivityDefined = watch('areaOfActivityDefined')

  const { showToast } = useToast()

  const getProfile = useCallback(() => {
    setIsLoading(true)

    return dispatch(getProfileById(perfilId))
      .then(data => {
        setValue('perfilName', data?.desc ?? '')
        setValue('specialStepFilter', data?.specialStepFilter ?? false)
        setValue('intermediaryPerfil', data?.intermediaryPerfil ?? false)
        setValue('areaOfActivityDefined', data?.areaOfActivityDefined ?? false)
        setValue('urlPainel', data?.urlPainel ?? '')
        setValue('displayAgencies', data?.displayAgencies ?? false)
        setValue('resourceAntecipation', data?.resourceAntecipation ?? false)
        setValue('bankingIntegrations', data?.bankingIntegrations ?? false)
      })
      .catch(() => {
        showToast({ type: 'error', message: 'Ocorreu um erro ao buscar os profiles.' })
      })
      .finally(() => setIsLoading(false))
  }, [dispatch, perfilId])

  const goBack = () => {
    window?.history?.length <= 2 ? navigate(`/`) : navigate(-1)
  }

  const handleClickSave = useCallback(
    data => {
      if (
        checkIfUserDoesNotPermission(PERMISSIONS.PROFILE_PERMISSIONS, [
          PERMISSIONS_ACTIONS.ADD,
          PERMISSIONS_ACTIONS.EDIT,
        ])
      ) {
        return
      }

      setIsLoading(true)

      const newData = { ...data }

      if (newData?.areaOfActivityDefined) {
        setValue('urlPainel', '')
        newData.urlPainel = ''
      }

      dispatch(editProfile(perfilId, { ...newData }))
        .then(() => {
          showToast({
            type: 'success',
            message: 'Campos salvos com sucesso.',
          })
          return setIsLoading(false)
        })
        .catch(() => {
          showToast({
            type: 'error',
            message: 'Ocorreu um erro ao salvar os dados. Tente novamente mais tarde.',
          })
          return setIsLoading(false)
        })
    },
    [perfilId]
  )

  function handleClickCancel() {
    getProfile()
  }

  useEffect(() => {
    getProfile()
  }, [perfilId])

  return (
    <Grid className={styles.page}>
      <Loader hasBackdrop open={isLoading} label="Aguarde..." />
      <HeaderTitle title="Perfil" backButtonAction={goBack}>
        <Grid style={{ display: 'flex', gap: '8px' }}>
          <Button className={[styles.buttonRed, styles.button]} onClick={() => handleClickCancel()}>
            Cancelar
          </Button>
          <Button
            className={[styles.buttonFullBlue, styles.button]}
            onClick={handleSubmit(handleClickSave)}
          >
            Salvar
          </Button>
        </Grid>
      </HeaderTitle>
      <FormProvider {...methods}>
        <form className={styles.container} id="perfil">
          <Grid className={[styles.subContainer, styles.subContainerWithInput]}>
            <InputLabel className={styles.labelTitle}>Dados principais</InputLabel>
            <Grid className={styles.wrapper}>
              <BasicInput
                label="Nome"
                name="perfilName"
                defaultValue={perfilName ?? ''}
                labelClasses={styles.label}
                containerClasses={styles.inputContainer}
                disabled
              />
            </Grid>
          </Grid>
          <Grid className={[styles.subContainer, styles.subContainerWithInput]}>
            <InputLabel className={styles.labelTitle}>Painel</InputLabel>
            <Grid className={styles.wrapper}>
              <Controller
                name="areaOfActivityDefined"
                control={control}
                defaultValue={false}
                render={({ value, onChange }) => {
                  return (
                    <div className={styles.switchDiv}>
                      <Typography component="h3" className={styles.switchLabel}>
                        Definido por área de atuação
                        <HelpInfoButton
                          title="Se assinalado, a URL do painel será definido no cadastro de Áreas de atuação."
                          placement="bottom"
                        />
                      </Typography>
                      <FormControlLabel
                        className={styles.switch}
                        control={
                          <SwitchComponent
                            name="areaOfActivityDefined"
                            checked={value}
                            onChange={e => onChange(e.target.checked)}
                          />
                        }
                      />
                    </div>
                  )
                }}
              />

              <BasicInput
                label={
                  <div className={styles.switchLabel}>
                    URL do painel
                    <HelpInfoButton
                      style={{ marginTop: '-9px' }}
                      title="Define a URL que será aberta quando acessado o menu principal “Painel”."
                      placement="bottom"
                    />
                  </div>
                }
                name="urlPainel"
                labelClasses={styles.label}
                disabled={areaOfActivityDefined}
                containerClasses={styles.inputContainer}
                rules={{
                  maxLength: {
                    value: 255,
                    message: `Esse campo aceita no máximo 255 caracteres`,
                  },
                }}
              />
            </Grid>
          </Grid>
          <Grid className={styles.subContainer}>
            <InputLabel className={styles.labelTitle}>Configurações gerais</InputLabel>
            <Grid className={styles.wrapper}>
              <Controller
                name="intermediaryPerfil"
                control={control}
                defaultValue={false}
                render={({ value, onChange }) => {
                  return (
                    <div className={styles.switchDiv}>
                      <Typography component="h3" className={styles.switchLabel}>
                        Perfil de intermediária
                        <HelpInfoButton
                          title="Se assinalado, indica que o perfil considera que o usuário sempre está vinculado a uma intermediária, afetando algumas partes do sistema, que vai buscar parâmetros nas Áreas de atuação, tais como filtros de widgets e relatórios para exibir."
                          placement="bottom"
                        />
                      </Typography>
                      <FormControlLabel
                        className={styles.switch}
                        control={
                          <SwitchComponent
                            name="intermediaryPerfil"
                            checked={value}
                            onChange={e => onChange(e.target.checked)}
                          />
                        }
                      />
                    </div>
                  )
                }}
              />

              <Controller
                name="specialStepFilter"
                control={control}
                defaultValue={false}
                render={({ value, onChange }) => {
                  return (
                    <div className={styles.switchDiv}>
                      <Typography component="h3" className={styles.switchLabel}>
                        <span>Filtro de etapas especial</span>
                        <HelpInfoButton
                          title="O filtro 'por Etapa', na lista de chamados, só mostra opções que tenham chamados nelas."
                          placement="bottom"
                        />
                      </Typography>
                      <FormControlLabel
                        className={styles.switch}
                        control={
                          <SwitchComponent
                            name="specialStepFilter"
                            checked={value}
                            onChange={e => onChange(e.target.checked)}
                          />
                        }
                      />
                    </div>
                  )
                }}
              />
              <Controller
                name="displayAgencies"
                control={control}
                defaultValue={false}
                render={({ value, onChange }) => {
                  return (
                    <div className={styles.switchDiv}>
                      <Typography component="h3" className={styles.switchLabel}>
                        <span>Exibir intermediárias nas listagens</span>
                        <HelpInfoButton
                          title="Se assinalado, exibe uma coluna e filtro “Intermediária” nas listagens de Recebimentos e Pagamentos."
                          placement="bottom"
                        />
                      </Typography>
                      <FormControlLabel
                        className={styles.switch}
                        control={
                          <SwitchComponent
                            name="displayAgencies"
                            checked={value}
                            onChange={e => onChange(e.target.checked)}
                          />
                        }
                      />
                    </div>
                  )
                }}
              />
              <Controller
                name="resourceAntecipation"
                control={control}
                defaultValue={false}
                render={({ value, onChange }) => {
                  return (
                    <div className={styles.switchDiv}>
                      <Typography component="h3" className={styles.switchLabel}>
                        <span>Antecipação de recursos</span>
                        <HelpInfoButton
                          title="Se assinalado, exibe funcionalidade de antecipação de recursos aos credores e filtros na área financeira."
                          placement="bottom"
                        />
                      </Typography>
                      <FormControlLabel
                        className={styles.switch}
                        control={
                          <SwitchComponent
                            name="resourceAntecipation"
                            checked={value}
                            onChange={e => onChange(e.target.checked)}
                          />
                        }
                      />
                    </div>
                  )
                }}
              />
              <Controller
                name="bankingIntegrations"
                control={control}
                defaultValue={false}
                render={({ value, onChange }) => {
                  return (
                    <div className={styles.switchDiv}>
                      <Typography component="h3" className={styles.switchLabel}>
                        <span>Integrações bancárias</span>
                        <HelpInfoButton
                          title="Se assinalado, exibe funcionalidade de geração de documentos bancários de pagamento (transferências)."
                          placement="bottom"
                        />
                      </Typography>
                      <FormControlLabel
                        className={styles.switch}
                        control={
                          <SwitchComponent
                            name="bankingIntegrations"
                            checked={value}
                            onChange={e => onChange(e.target.checked)}
                          />
                        }
                      />
                    </div>
                  )
                }}
              />
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </Grid>
  )
}
